import * as React from 'react';
import { checkboxClasses, formControlLabelClasses, InputLabel, SxProps, Theme } from '@mui/material';
import { archerConstants, customArcherColors } from '../../utils';
import { ListItem } from '../ListItem';
import { Collapse } from '../Collapse';
import { Icon, IconKey } from '../Icon';
import { List } from '../List';
import { ListItemText } from '../ListItemText';
import { Typography } from '../Typography';
import { Tooltip } from '../Tooltip';
import { IconButton } from '../IconButton';
import { _DeprecatedCheckbox as Checkbox, _DeprecatedCheckboxProps } from '../_DeprecatedCheckbox';
import { RecursiveListProps } from './RecursiveList';

export type Id = string;

export type ReorderableListItem = {
  id: Id;
  content: string | React.ReactNode;
};

export interface RecursiveListItemProps {
  id?: string;
  label: string;
  expandable?: boolean;
  searchString?: RecursiveListProps['searchString'];
  disabled?: boolean;
  cascade?: boolean;
  className?: string;
  dataListItem: RecursiveListDataItem & CascadableItem;
  deleteDisabled?: boolean;
  isSelectedList?: RecursiveListDataItem['isSelectedList'];
  forceShowRecursion?: RecursiveListDataItem['isSelectedList'];
  selected?: boolean;
  showCascade?: boolean;
  showChildren?: boolean;
  showInnerChildren: boolean;
  parentSelectable?: RecursiveListProps['parentSelectable'];
  tabFocus?: boolean;
  clickHandler?: RecursiveListProps['clickHandler'];
  doubleClickHandler: RecursiveListProps['doubleClickHandler'];
  expandChildClickHandler?: RecursiveListProps['expandChildClickHandler'];
  isLoading?: boolean;
  toggleCascadeHandler?: RecursiveListProps['toggleCascadeHandler'];
  toolTipExpand: RecursiveListProps['toolTipExpand'];
  toolTipCollapse: RecursiveListProps['toolTipCollapse'];
  toolTipRemove: RecursiveListProps['toolTipRemove'];
  toolTipCannotRemove: RecursiveListProps['toolTipCannotRemove'];
  textCascade?: RecursiveListProps['textCascade'];
  sx?: SxProps<Theme>;
}

export interface RecursiveListDataItemBase {
  label: string;
  id: string;
  expandable?: boolean;
  preSelected?: boolean;
  selected: boolean;
  tabFocus?: boolean;
  children: RecursiveListDataItem[];
  showChildren?: boolean;
  showInnerChildren?: boolean;
  deleteDisabled?: boolean;
  disabled?: boolean;
  isSelectedList?: boolean;
  forceShowRecursion?: boolean;
  previousItem?: RecursiveListDataItem;
  nextItem?: RecursiveListDataItem;
  searchString?: RecursiveListItemProps['searchString'];
  leftIcon?: IconKey;
  reorderable?: boolean;
  orderedList?: ReorderableListItem[];
}

export interface CascadableItem {
  cascade?: boolean;
  showCascade?: boolean;
}

export type RecursiveListDataItem = CascadableItem & RecursiveListDataItemBase;

const styles: Record<string, SxProps<Theme>> = {
  listWithChildren: {
    display: 'flex',
    flexDirection: 'row',
    paddingY: 0,
    paddingLeft: 2,
    paddingRight: 0,
    width: '100%',
    '& :first-child': {
      paddingTop: 0,
      paddingBottom: 0
    }
  },
  listItemRoot: {
    padding: 0,
    '&.Mui-selected': { backgroundColor: 'transparent' },
    '&:focus': { backgroundColor: archerConstants.archerHover }
  },
  listItemRootLabel: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    margin: 0,
    padding: 0,
    '& :first-child': {
      paddingTop: 0,
      paddingBottom: 0,
      '&.Mui-focusVisible': {
        backgroundColor: archerConstants.archerHover,
        borderRadius: '0'
      }
    },
    '& svg': {
      fontSize: '1.25rem'
    },
    width: '100%'
  },
  childListItemExpander: {
    padding: 0,
    width: 'auto'
  },
  recursiveChildList: {
    paddingY: 0,
    paddingLeft: 2,
    paddingRight: 0
  },
  listItemText: {
    '&:focus': { backgroundColor: archerConstants.archerHover }
  },
  childListItemText: {
    padding: 0,
    '&:focus': { backgroundColor: archerConstants.archerHover }
  },
  childListItemTextNoSelect: {
    padding: 0
  },
  iconButton: {
    margin: 0,
    padding: 0,
    '& :hover svg, &:focus svg': {
      display: 'inline',
      color: customArcherColors.alertColors && customArcherColors.alertColors.error,
      '&:focus': {
        background: 'primary.main'
      },
      '& :focus > div > span': {
        color: 'text.primary'
      },
      outline: 'none',
      '& svg': {
        display: 'none'
      }
    },
    '& [class*="MuiSvgIcon-fontSizeSmall"]': {
      fontSize: '1rem'
    }
  },
  toolTip: {
    margin: 0,
    padding: 0
  },
  iconListItem: {
    justifyContent: 'flex-end',
    maxWidth: '3rem'
  },
  cascadeListItem: {
    width: 'auto',
    justifyContent: 'flex-end',
    [`& .${checkboxClasses['colorPrimary']}.Mui-checked:hover`]: {
      backgroundColor: 'transparent'
    },
    [`& .${checkboxClasses['colorPrimary']}:hover`]: {
      backgroundColor: 'transparent'
    },
    '& label': {
      marginRight: '0.7rem',
      [`& .${formControlLabelClasses['label']}`]: {
        display: 'none'
      }
    }
  }
};

const RecursiveListItemBase = ({
  dataListItem,
  clickHandler,
  searchString = '',
  disabled,
  selected,
  showChildren,
  parentSelectable,
  expandChildClickHandler,
  isSelectedList,
  forceShowRecursion,
  doubleClickHandler,
  toggleCascadeHandler,
  toolTipExpand,
  toolTipCollapse,
  toolTipCannotRemove,
  toolTipRemove,
  textCascade,
  sx
}: RecursiveListItemProps) => {
  let showParentAndExpand = false;
  // Test fpr SonarQube
  const {
    listWithChildren,
    listItemRoot,
    listItemRootLabel,
    recursiveChildList,
    childListItemExpander,
    listItemText,
    childListItemText,
    childListItemTextNoSelect,
    toolTip,
    iconButton,
    iconListItem,
    cascadeListItem
  } = styles;
  const [open, toggleOpen] = React.useState<boolean>(showChildren || showParentAndExpand);
  let cascadeItemsGroup: _DeprecatedCheckboxProps['checkboxGroup'] = { items: [] };
  // TODO: translate "cascade toggle" string
  if (isSelectedList && dataListItem.showCascade) {
    cascadeItemsGroup = {
      items: [
        {
          color: 'primary',
          description: `${dataListItem.label}${textCascade ? ` ${textCascade}` : ''}`,
          id: dataListItem.id,
          isChecked: dataListItem.cascade ? dataListItem.cascade : false,
          isEnabled: true
        }
      ]
    };
  }
  if (isSelectedList && !dataListItem.selected && !forceShowRecursion) {
    return null;
  }
  const searchItem = (listItem: any) => {
    if (searchString ? searchString.length <= 0 : '') {
      return true;
    }
    if (
      listItem.children &&
      listItem.children.some(
        (child: RecursiveListItemProps) =>
          child.label && child.label.toLowerCase().includes(searchString && searchString.length > 0 ? searchString : '')
      )
    ) {
      showParentAndExpand = true;
      return true;
    }
    if (listItem.label.toLowerCase().includes(searchString)) {
      showParentAndExpand = true;
      return true;
    }
    showParentAndExpand = false;
    return false;
  };
  const searchChild = (childItem: any) => {
    if (childItem.label && childItem.label.toLowerCase().includes(searchString)) {
      return true;
    }
    return false;
  };
  const { children } = dataListItem;
  const handleItemClick = (item: any) => {
    if (clickHandler) {
      clickHandler(item);
    }
  };
  const handleItemDoubleClick = (item: any) => {
    if (doubleClickHandler) {
      doubleClickHandler(item);
    }
  };
  const handleKeyDown = (event: React.KeyboardEvent, item: any) => {
    if (event.keyCode === 32 || event.keyCode === 13) {
      handleItemDoubleClick(item);
    }
  };
  const handleClickExpand = (item: any) => {
    toggleOpen(!open);
    if (expandChildClickHandler) {
      expandChildClickHandler(item);
    }
  };
  const handleCascadeToggle = (item: any) => {
    if (toggleCascadeHandler) {
      toggleCascadeHandler(item);
    }
  };
  return (
    <React.Fragment>
      {/* Renders the parent*/}
      {searchItem(dataListItem) && (
        <ListItem
          key={dataListItem.id}
          id={`listitem-root-${dataListItem.id}`}
          selected={selected}
          disabled={disabled}
          sx={[listItemRoot, sx] as SxProps<Theme>}
          aria-label={dataListItem.label}
        >
          <InputLabel htmlFor={`listitem-root-${dataListItem.id}`} aria-label={dataListItem.label} />
          {(children && children.length > 0) || dataListItem.expandable ? (
            [
              <List dense key={dataListItem.id} sx={listWithChildren}>
                <ListItem
                  button
                  onClick={() => handleClickExpand(dataListItem)}
                  onKeyDown={e => handleKeyDown(e, dataListItem)}
                  component="li"
                  sx={childListItemExpander}
                  id={`expand-${dataListItem.id}`}
                  title={open ? `${toolTipCollapse} ${dataListItem.label}` : `${toolTipExpand} ${dataListItem.label}`}
                  aria-label={open ? `${toolTipCollapse} ${dataListItem.label}` : `${toolTipExpand} ${dataListItem.label}`}
                >
                  <InputLabel
                    htmlFor={`expand-${dataListItem.id}`}
                    aria-label={open ? `${toolTipCollapse} ${dataListItem.label}` : `${toolTipExpand} ${dataListItem.label}`}
                  />
                  {!isSelectedList || forceShowRecursion ? <Icon icon={open ? 'ArrowDropDown' : 'ArrowRight'} fontSize="small" /> : null}
                </ListItem>
                {parentSelectable ? (
                  <ListItem
                    button
                    onClick={() => handleItemClick(dataListItem)}
                    onDoubleClick={() => handleItemDoubleClick(dataListItem)}
                    onKeyDown={e => handleKeyDown(e, dataListItem)}
                    sx={dataListItem.deleteDisabled ? childListItemTextNoSelect : childListItemText}
                    component="li"
                    id={`parent-selectable-${dataListItem.id}`}
                    tabIndex={dataListItem.deleteDisabled ? -1 : 0}
                    aria-label={dataListItem.label}
                  >
                    <InputLabel htmlFor={`parent-selectable-${dataListItem.id}`} aria-label={dataListItem.label} />
                    <ListItemText primary={<Typography>{dataListItem.label}</Typography>} />
                  </ListItem>
                ) : (
                  <ListItem
                    sx={dataListItem.deleteDisabled ? childListItemTextNoSelect : childListItemText}
                    id={dataListItem.id}
                    aria-label={dataListItem.label}
                    onKeyDown={e => handleKeyDown(e, dataListItem)}
                    tabIndex={dataListItem.deleteDisabled ? -1 : 0}
                  >
                    <InputLabel htmlFor={dataListItem.id} aria-label={dataListItem.label} />
                    <ListItemText primary={<Typography>{dataListItem.label}</Typography>} />
                  </ListItem>
                )}
                {isSelectedList && (
                  <React.Fragment>
                    {dataListItem.showCascade && (
                      <ListItem sx={cascadeListItem}>
                        <Checkbox
                          disableRipple
                          noFormControl
                          checkboxGroup={cascadeItemsGroup}
                          checked={dataListItem.cascade}
                          onChange={() => handleCascadeToggle(dataListItem)}
                        />
                      </ListItem>
                    )}
                    <ListItem sx={iconListItem}>
                      <Tooltip sx={toolTip} title={dataListItem.deleteDisabled ? toolTipCannotRemove : toolTipRemove}>
                        <IconButton
                          tabIndex={-1}
                          fontSize="medium"
                          onClick={() => handleItemDoubleClick(dataListItem)}
                          icon={dataListItem.deleteDisabled ? 'NotInterested' : 'RemoveCircle'}
                          sx={iconButton}
                        />
                      </Tooltip>
                    </ListItem>
                  </React.Fragment>
                )}
              </List>
            ]
          ) : (
            <List dense sx={[listItemRootLabel, sx] as SxProps<Theme>}>
              {dataListItem.leftIcon && (
                <ListItem>
                  <Icon icon={dataListItem.leftIcon} fontSize="small" color="primary" />
                </ListItem>
              )}
              <ListItem
                button
                component="li"
                sx={listItemText}
                onClick={() => handleItemClick(dataListItem)}
                onDoubleClick={() => handleItemDoubleClick(dataListItem)}
                onKeyDown={e => handleKeyDown(e, dataListItem)}
              >
                <ListItemText primary={<Typography title={dataListItem.label}>{dataListItem.label}</Typography>} disableTypography />
              </ListItem>
              {isSelectedList && (
                <React.Fragment>
                  {dataListItem.showCascade && (
                    <ListItem sx={cascadeListItem}>
                      <Checkbox
                        disableRipple
                        noFormControl
                        checkboxGroup={cascadeItemsGroup}
                        checked={dataListItem.cascade}
                        onChange={() => handleCascadeToggle(dataListItem)}
                      />
                    </ListItem>
                  )}
                  <ListItem sx={iconListItem}>
                    <Tooltip sx={toolTip} title={dataListItem.deleteDisabled ? toolTipCannotRemove : toolTipRemove}>
                      <IconButton
                        tabIndex={-1}
                        fontSize="medium"
                        onClick={() => handleItemDoubleClick(dataListItem)}
                        icon={dataListItem.deleteDisabled ? 'NotInterested' : 'RemoveCircle'}
                        sx={iconButton}
                      />
                    </Tooltip>
                  </ListItem>
                </React.Fragment>
              )}
            </List>
          )}
        </ListItem>
      )}
      {/* Renders the Children*/}
      <Collapse isOpen={open} timeout="auto" unmountOnExit>
        {children &&
          children.length > 0 &&
          children.map((child: RecursiveListItemProps['dataListItem']) => {
            return (
              searchChild(child) && (
                <List dense key={child.id} sx={recursiveChildList} role="list">
                  {children.length > 0 ? (
                    <RecursiveListItem
                      id={child.id}
                      selected={child.selected}
                      dataListItem={child}
                      {...(child.tabFocus && { tabFocus: true })}
                      clickHandler={handleItemClick}
                      doubleClickHandler={doubleClickHandler}
                      expandChildClickHandler={expandChildClickHandler}
                      key={child.id}
                      label={child.label}
                      searchString={child.searchString}
                      showInnerChildren={false}
                      showChildren={false}
                      forceShowRecursion={forceShowRecursion}
                      isSelectedList={isSelectedList}
                      parentSelectable={parentSelectable}
                      toggleCascadeHandler={handleCascadeToggle}
                      toolTipExpand={toolTipExpand}
                      toolTipCollapse={toolTipCollapse}
                      toolTipCannotRemove={toolTipCannotRemove}
                      toolTipRemove={toolTipRemove}
                    />
                  ) : null}
                </List>
              )
            );
          })}
      </Collapse>
    </React.Fragment>
  );
};

const RecursiveListItem = React.memo(RecursiveListItemBase);

RecursiveListItem.displayName = 'RecursiveListItem';

export { RecursiveListItem };
